import { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import "./App.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Search, ScanLine, Save, Trash2, FileText, UserSearch, X, Plus, CheckCircle2, Users, FileImage, Settings, Shield, FlaskConical, LogOut, Camera, SlidersHorizontal, MessageSquareWarning, ShoppingBag, UserCheck, CalendarDays, Rocket, ListChecks, ScrollText } from "lucide-react";
import { FORCE_POINT_UI } from "./lib/forcePointUi.js";
import { TEST_BUILD } from "./lib/testBuild.js";
import { useWbSettings } from "./lib/useWbSettings.js";
import { removeDarkBackground } from "./lib/imageProcessing.js";
import { useHeatSettings } from "./lib/useHeatSettings.js";
import { generateHeatmap } from "./lib/heatmap.js";
import { detectFootRegions } from "./lib/detectFootRegions.js";
import { useDebouncedValue } from "./lib/useDebouncedValue.js";
import { mskTodayStr } from "./lib/mskDate.js";
import mockScanUrl from "./assets/mock-scan.webp";
import { saveScanExtras, queueStorage, queueFiles, removeQueuedFile, buildPurchaseIntents, saveDocumentSnapshot } from "./lib/saveQueue.js";

// Тестовый режим — реальное фото стоп вместо синтетической заглушки, чтобы можно было
// проверять автоопределение областей/поворот/отчёт на правдоподобном изображении.
// Re-encoded to JPEG via canvas rather than handed through as the source .webp — the raw scan
// upload path only accepts JPEG/PNG magic bytes (real scans are always one of those), and a mock
// scan needs to behave identically through that same pipeline for testing to mean anything.
async function generateMockScan() {
  const resp = await fetch(mockScanUrl);
  const blob = await resp.blob();
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext("2d").drawImage(bitmap, 0, 0);
  return canvas.toDataURL("image/jpeg", 0.92);
}
import { AuthProvider, useAuth } from "./contexts/AuthContext.jsx";
import { ScannerProvider, useScanner } from "./contexts/ScannerContext.jsx";
import { ConnectivityProvider } from "./contexts/ConnectivityContext.jsx";
import { PatientsProvider, usePatients } from "./contexts/PatientsContext.jsx";
import NewPatientModal from "./components/NewPatientModal.jsx";
import SaveDetailsModal from "./components/SaveDetailsModal.jsx";
import ScanSavedPanel from "./components/ScanSavedPanel.jsx";
import PatientReportDetailsModal from "./components/PatientReportDetailsModal.jsx";
import LoadError from "./components/LoadError.jsx";
import YClientsRecordPickerModal from "./components/YClientsRecordPickerModal.jsx";
import CatalogPickerModal from "./components/CatalogPickerModal.jsx";
import ProductQuantityPriceRow, { formatPrice } from "./components/ProductQuantityPriceRow.jsx";
import LocalPhotosModal from "./components/LocalPhotosModal.jsx";
import CameraCaptureModal from "./components/CameraCaptureModal.jsx";
import PhotoLightboxModal from "./components/PhotoLightboxModal.jsx";
import FeedbackModal from "./components/FeedbackModal.jsx";
import OfflineAlert from "./components/OfflineAlert.jsx";
import { useConfirm } from "./components/ConfirmDialog.jsx";
import { installErrorLog } from "./lib/errorLog.js";
import { clientLog, shortId } from "./lib/clientLog.js";
import { buildProductGroupTree } from "./lib/productGroups.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import AdminPage from "./pages/AdminPage.jsx";
import FieldConfigPage from "./pages/FieldConfigPage.jsx";
import DocumentsPage from "./pages/DocumentsPage.jsx";
import PatientsPage from "./pages/PatientsPage.jsx";
import PatientCard from "./pages/PatientCard.jsx";
import SettingsPage from "./pages/SettingsPage.jsx";
import ScanLabPage from "./pages/ScanLabPage.jsx";
import YClientsPage from "./pages/YClientsPage.jsx";
import RoadmapPage from "./pages/RoadmapPage.jsx";
import SyncLogPage from "./pages/SyncLogPage.jsx";
import PointLogsPage from "./pages/PointLogsPage.jsx";
import ReconcilePage from "./pages/ReconcilePage.jsx";
import JournalPage from "./pages/JournalPage.jsx";
import ImageViewer from "./components/ImageViewer.jsx";
import ScanParamsPanel from "./components/ScanParamsPanel.jsx";
import { compositeImage } from "./lib/compositeImage.js";
import { printReport, generateReportHtml, cropFootImage } from "./lib/generateReport.js";
import { buildAnnotatedFootCrops } from "./lib/scanPreview.js";
import api from "./api.js";
import PatientAlias from "./components/PatientAlias.jsx";
import BrandMark from "./components/BrandMark.jsx";
import { APP_NAME, APP_TITLE } from "./lib/brand.js";
import { needsPatientReportDetails } from "./lib/patientReportDetails.js";

export default function App() {
  return (
    <AuthProvider>
      <ScannerProvider>
        <ConnectivityProvider>
          <PatientsProvider>
            <AppInner />
          </PatientsProvider>
          <OfflineAlert />
        </ConnectivityProvider>
      </ScannerProvider>
    </AuthProvider>
  );
}

// ── Products card (separate block below ScanParamsPanel) ─────────────────────

// Количество для каждого выбранного товара — отсутствие записи в details (старые сканы без этого
// поля, или товар выбран, но ещё не тронут степпером) значит "1 штука".
function countsFromSelection(ids, details) {
  const counts = {};
  for (const id of ids) counts[id] = details?.[id]?.quantity ?? 1;
  return counts;
}

function ScannerProductsCard({ onNavigate, selectedIds = [], onSelectionChange, productDetails = {}, onProductDetailsChange }) {
  const { user } = useAuth();
  const canDelete = !!(user?.isCentral && user?.role === "admin");
  const [products, setProducts] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    Promise.all([api.listProducts(), api.listProductGroups()])
      .then(([p, g]) => { setProducts(p); setProductGroups(g); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const removeProduct = (id) => {
    onSelectionChange?.(selectedIds.filter(x => x !== id));
    const rest = { ...productDetails };
    delete rest[id];
    onProductDetailsChange?.(rest);
  };

  const toggleProduct = (id) => {
    if (selectedIds.includes(id)) {
      removeProduct(id);
      return;
    }
    const product = products.find(p => p.id === id);
    onSelectionChange?.([...selectedIds, id]);
    onProductDetailsChange?.({ ...productDetails, [id]: { quantity: 1, price: product?.price ?? null } });
  };

  const updateDetails = (id, patch) => {
    onProductDetailsChange?.({ ...productDetails, [id]: { ...productDetails[id], ...patch } });
  };

  const deleteProduct = async (id) => {
    try {
      await api.deleteProduct(id);
      setProducts(prev => prev.filter(p => p.id !== id));
      if (selectedIds.includes(id)) removeProduct(id);
    } catch {
      // picker stays open, list just doesn't update — user can retry
    }
  };

  const selectedProducts = products.filter(p => selectedIds.includes(p.id));
  const groupTree = buildProductGroupTree(products, productGroups);

  return (
    <div className="scanner-products flex flex-col flex-1 min-h-0 rounded-2xl overflow-hidden" style={{ background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05)" }}>
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 shrink-0">
        <span className="text-sm font-semibold text-slate-700">Рекомендуемые товары{selectedProducts.length > 0 && <span className="ml-2 text-xs text-slate-400">{selectedProducts.length}</span>}</span>
        <button
          onClick={() => setPickerOpen(true)}
          className="flex items-center gap-1 text-[11px] text-blue-500 hover:text-blue-700 font-medium transition-colors"
        >
          Каталог
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      </div>

      <div className="p-3 flex flex-col gap-2.5 flex-1 min-h-0 overflow-y-auto">
        {loading ? (
          <div className="h-8 rounded-lg bg-slate-100 animate-pulse" />
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-4 px-3 bg-slate-50 rounded-xl border border-dashed border-slate-200">
            <span className="text-2xl">👟</span>
            <p className="text-[11px] text-slate-400 text-center leading-snug">Каталог товаров не настроен</p>
            {user?.role === "admin" && <button onClick={() => onNavigate("fieldconfig")} className="text-[11px] text-blue-500 hover:text-blue-700 font-semibold transition-colors">
              Настроить каталог →
            </button>}
          </div>
        ) : (
          <>
            {selectedProducts.length > 0 ? (
              <div className="flex flex-col gap-1.5">
                {selectedProducts.map(p => {
                  const details = productDetails[p.id] || { quantity: 1, price: p.price ?? null };
                  return (
                    <ProductQuantityPriceRow
                      expanded
                      key={p.id}
                      name={p.name}
                      categoryIcon={p.category === "insole" ? "👟" : "✨"}
                      catalogPrice={p.price}
                      details={details}
                      onChange={patch => updateDetails(p.id, patch)}
                      onRemove={() => removeProduct(p.id)}
                    />
                  );
                })}
                <div className="text-right text-[11px] font-semibold text-slate-500 pr-1">
                  Итого: {formatPrice(selectedProducts.reduce((s, p) => {
                    const d = productDetails[p.id] || { quantity: 1, price: p.price ?? null };
                    return s + (d.price != null ? d.price * (d.quantity ?? 1) : 0);
                  }, 0))}
                </div>
              </div>
            ) : (
              <div className="flex flex-1 min-h-[116px] flex-col items-center justify-center gap-2 px-3 py-4 text-center rounded-xl bg-slate-50/70">
                <ShoppingBag className="h-6 w-6 text-slate-300" strokeWidth={1.5} />
                <p className="text-xs text-slate-500">Стельки и сопутствующие товары</p>
                <Button size="sm" variant="outline" className="gap-1.5 bg-white mt-1" onClick={() => setPickerOpen(true)}><Plus className="h-3.5 w-3.5" />Выбрать товары</Button>
              </div>
            )}
            {selectedProducts.length > 0 && <Button size="sm" variant="outline" className="w-full gap-1.5" onClick={() => setPickerOpen(true)}>
              Выбрать товары
            </Button>}
          </>
        )}
      </div>

      {pickerOpen && (
        <CatalogPickerModal
          title="Товары и стельки"
          groupTree={groupTree}
          selectedIds={selectedIds}
          onToggle={toggleProduct}
          onClose={() => setPickerOpen(false)}
          onDeleteItem={canDelete ? deleteProduct : undefined}
        />
      )}
    </div>
  );
}

// ── Exercises card (same pattern as products, picked via modal) ──────────────
function ScannerExercisesCard({ onNavigate, selectedIds = [], onSelectionChange }) {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    api.listExercises()
      .then(setExercises)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleExercise = (id) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter(x => x !== id)
      : [...selectedIds, id];
    onSelectionChange?.(next);
  };

  const selectedExercises = exercises.filter(e => selectedIds.includes(e.id));
  const groups = [
    { key: "all", label: null, items: exercises.map(e => ({
      id: e.id, name: e.name,
      thumb: e.hasPhoto
        ? <img src={api.exercisePhotoUrl(e.id)} alt={e.name} className="w-9 h-9 rounded-lg object-cover border border-slate-200" />
        : <span className="text-lg">🏃</span>,
    })) },
  ];

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05)" }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <span className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Упражнения</span>
        <button
          onClick={() => onNavigate("fieldconfig")}
          className="flex items-center gap-1 text-[11px] text-blue-500 hover:text-blue-700 font-medium transition-colors"
        >
          Каталог
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="m9 18 6-6-6-6"/></svg>
        </button>
      </div>

      <div className="p-3 flex flex-col gap-2.5">
        {loading ? (
          <div className="h-8 rounded-lg bg-slate-100 animate-pulse" />
        ) : exercises.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-4 px-3 bg-slate-50 rounded-xl border border-dashed border-slate-200">
            <span className="text-2xl">🏃</span>
            <p className="text-[11px] text-slate-400 text-center leading-snug">Список упражнений не настроен</p>
            <button onClick={() => onNavigate("fieldconfig")} className="text-[11px] text-blue-500 hover:text-blue-700 font-semibold transition-colors">
              Настроить каталог →
            </button>
          </div>
        ) : (
          <>
            {selectedExercises.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {selectedExercises.map(e => (
                  <span key={e.id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-[11px] font-medium">
                    🏃 {e.name}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-slate-400">Ничего не выбрано</p>
            )}
            <Button size="sm" variant="outline" className="self-start gap-1.5" onClick={() => setPickerOpen(true)}>
              Выбрать упражнения
            </Button>
          </>
        )}
      </div>

      {pickerOpen && (
        <CatalogPickerModal
          title="Упражнения"
          groups={groups}
          selectedIds={selectedIds}
          onToggle={toggleExercise}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

function LeftNav({ page, navigate, user, logout }) {
  const { status } = useScanner();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Сканер/Журнал — недоступны менеджеру (сканирование только через сотрудников) и на central
  // (там физически нет сканера). Настройки — только на central, менеджеру они, в отличие от
  // сканера, доступны (менеджер может всё, кроме самого сканирования).
  const hideOperational = user?.role === "manager" || user?.isCentral;
  const showScanner = !hideOperational || FORCE_POINT_UI; // see FORCE_POINT_UI comment above
  const hideSettings = user?.isCentral;
  const navItems = [
    ...(showScanner ? [{ id: "scanner", label: "Сканер", icon: ScanLine }] : []),
    ...(showScanner ? [{ id: "journal", label: "Журнал", icon: CalendarDays }] : []),
    { id: "patients", label: "Пациенты",        icon: Users      },
    { id: "scans",    label: "Сканирования",    icon: FileImage  },
    ...(!hideSettings ? [{ id: "settings", label: "Настройки", icon: Settings }] : []),
    ...(user?.role === "admin" ? [
      { id: "admin",       label: "Пользователи",        icon: Shield            },
      { id: "fieldconfig", label: "Конфигурация полей",  icon: SlidersHorizontal },
      { id: "lab",         label: "Лаборатория",         icon: FlaskConical      },
      { id: "yclients",    label: "YClients",            icon: ShoppingBag       },
      { id: "roadmap",     label: "Roadmap",             icon: Rocket            },
    ] : []),
    // Сверка пациентов с YClients — только центральный админ (реальная работа с точками не
    // задевает, там этой вкладки просто нет).
    ...(user?.role === "admin" && user?.isCentral ? [
      { id: "reconcile", label: "Разобрать", icon: UserCheck },
      { id: "sync-log",  label: "Лог синка", icon: ListChecks },
      { id: "point-logs", label: "Логи точек", icon: ScrollText },
    ] : []),
  ];

  const statusColor = status === "connected" ? "bg-emerald-400" : status === "connecting" ? "bg-amber-400 animate-pulse" : "bg-red-400";
  const initials = (user?.username || "U").slice(0, 2).toUpperCase();

  return (
    <nav className="flex flex-col flex-shrink-0" style={{ width: 220, minWidth: 220, background: "#0F172A" }}>
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-4 flex-shrink-0 border-b" style={{ height: 56, borderColor: "rgba(255,255,255,0.07)" }}>
        <div className="flex items-center justify-center rounded-lg flex-shrink-0 bg-white" style={{ width: 32, height: 32 }}>
          <BrandMark className="h-6 w-6" />
        </div>
        <div className="min-w-0" title={APP_TITLE}>
          <span className="font-bold text-white block" style={{ fontSize: 15, letterSpacing: "-0.2px" }}>{APP_NAME}</span>
          <span className="text-[10px] text-slate-400 block truncate">Диагностика стоп</span>
        </div>
        <span
          className={cn("rounded-full flex-shrink-0 ml-auto", statusColor)}
          style={{ width: 8, height: 8 }}
          title={status === "connected" ? "Сканер подключён" : status === "connecting" ? "Подключение..." : "Сканер не подключён"}
        />
      </div>

      {/* Navigation items */}
      <div className="flex flex-col flex-1 py-2">
        {navItems.map(({ id, label, icon: Icon }) => {
          const isActive = page === id || (page === "patient" && id === "patients");
          return (
            <button
              key={id}
              onClick={() => navigate(id)}
              className={cn(
                "flex items-center gap-2.5 w-full text-left transition-all",
                "border-l-2 border-transparent",
                isActive
                  ? "text-white font-semibold"
                  : "font-medium"
              )}
              style={{
                padding: "10px 16px",
                fontSize: 13,
                background: isActive ? "rgba(59,130,246,0.12)" : "transparent",
                borderLeftColor: isActive ? "#3B82F6" : "transparent",
                color: isActive ? "#fff" : "#94A3B8",
              }}
              onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.color = "#CBD5E1"; } }}
              onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#94A3B8"; } }}
            >
              <Icon style={{ width: 16, height: 16, flexShrink: 0, color: isActive ? "#60A5FA" : "currentColor", opacity: isActive ? 1 : 0.8 }} />
              {label}
            </button>
          );
        })}
      </div>

      {/* Report a problem — always visible regardless of role/page */}
      <div className="px-2 pb-1 flex-shrink-0">
        <button
          onClick={() => setFeedbackOpen(true)}
          className="flex items-center gap-2.5 w-full text-left transition-all rounded-lg"
          style={{ padding: "8px 10px", fontSize: 12, color: "#94A3B8" }}
          onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.04)"; e.currentTarget.style.color = "#CBD5E1"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#94A3B8"; }}
        >
          <MessageSquareWarning style={{ width: 14, height: 14, flexShrink: 0 }} />
          Сообщить о проблеме
        </button>
      </div>
      {feedbackOpen && <FeedbackModal page={page} onClose={() => setFeedbackOpen(false)} />}

      {/* Footer */}
      <div className="flex items-center gap-2.5 px-4 py-3 flex-shrink-0 border-t" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
        <div className="flex items-center justify-center rounded-full flex-shrink-0 font-bold" style={{ width: 30, height: 30, background: "rgba(59,130,246,0.18)", color: "#60A5FA", fontSize: 11 }}>
          {initials}
        </div>
        <div className="flex-1 min-w-0 leading-tight">
          <div className="text-xs truncate" style={{ color: "#64748B" }}>{user?.username}</div>
          <div title="Версия программы" style={{ fontSize: 10, color: "#475569" }}>v{__APP_VERSION__}</div>
        </div>
        <button
          onClick={logout}
          title="Выйти"
          className="transition-colors rounded p-1"
          style={{ background: "transparent", border: "none", cursor: "pointer", color: "#475569" }}
          onMouseEnter={e => { e.currentTarget.style.color = "#CBD5E1"; }}
          onMouseLeave={e => { e.currentTarget.style.color = "#475569"; }}
        >
          <LogOut style={{ width: 14, height: 14 }} />
        </button>
      </div>
    </nav>
  );
}

// Fresh per-scan state — reused by the initial useState and by "Очистить" so a new scan
// never inherits stale size/diseases/complaints from the previous one.
const INITIAL_SCAN_PARAMS = {
  patientId: "",
  // symptoms/footTypes/cornZones are the жалобы-redesign fields (complaintsFormat 2); diseases/
  // complaints/complaintsText/otherText are kept only so an old-format scan's data still has
  // somewhere to land when restored for read-only viewing (see loadDocumentForEditing).
  left:  { footSizeMm: "", symptoms: [], footTypes: [], cornZones: [], diseases: [], complaints: [], complaintsText: "", otherText: "" },
  right: { footSizeMm: "", symptoms: [], footTypes: [], cornZones: [], diseases: [], complaints: [], complaintsText: "", otherText: "" },
  generalSymptoms: [],
  shoeTypes: [],
  loadTypeId: "",
  weight: "",
  noteComment: "",
  noteRecommendations: "",
  internalNote: "",
  // YClients-запись, к которой привязано именно это сканирование (не пациент — один и тот же
  // пациент может прийти по разным записям в разные визиты).
  yclientsRecordId: null,
  // Форма в конце сканирования (см. ScanParamsPanel.jsx). messenger/messengerLink — на самом
  // деле поля ПАЦИЕНТА в YClients (доп.поля клиента), не скана — форма живёт тут просто потому,
  // что "конец сканирования" естественный момент их уточнить/подтвердить. recordLabelIds —
  // "категория записи" (YClients record_labels), это уже реально привязано к самому скану/визиту.
  messenger: "",
  messengerLink: "",
  recordLabelIds: [],
};

function AppInner() {
  const { user, loading, logout, setIdleLogoutBlocker } = useAuth();
  const { scan: doScanHW, scanning, error: scanError } = useScanner();
  const { confirm, ConfirmDialogElement } = useConfirm();

  const [page, setPage] = useState("scanner");
  const [patientCardId, setPatientCardId] = useState(null);
  const [mockMode, setMockMode] = useState(TEST_BUILD);
  // Set while the scanner screen is showing an already-saved document opened for editing (via
  // "Обследования" -> "Редактировать") rather than a fresh live scan — changes doSave to PATCH
  // the same document instead of creating a new one, and lets central-admin (no scanner
  // hardware) reach the scanner screen at all, but only in this editing mode.
  const [editingDocumentId, setEditingDocumentId] = useState(null);
  // Pre-жалобы-redesign scans (complaintsFormat 1) open for viewing only — see
  // loadDocumentForEditing. No more editing/saving over them; PDF export still works.
  const [editingReadOnly, setEditingReadOnly] = useState(false);

  useEffect(() => { installErrorLog(); }, []);

  useEffect(() => {
    if (user?.isCentral && page === "settings") {
      setPage("scans");
    }
    if (user?.role === "manager" && page === "scanner") {
      setPage("scans");
    }
    if (user?.isCentral && page === "scanner" && !editingDocumentId && !FORCE_POINT_UI) {
      setPage("scans");
    }
  }, [user, page, editingDocumentId]);

  const [pages, setPagesState] = useState([]);
  const [imageRevision, setImageRevision] = useState(0);
  const imageGenerationRef = useRef(0);
  const setPages = useCallback(value => { imageGenerationRef.current++; setPagesState(value); setImageRevision(revision => revision + 1); }, []);
  const pagesRef = useRef([]);
  useEffect(() => { pagesRef.current = pages; }, [pages]);
  const [photoFiles, setPhotoFiles] = useState([]); // supplementary photos queued for the next save, separate from scanned pages
  // Files already uploaded to the server for the document currently loaded here — without this,
  // the Scanner had no idea a just-saved (or previously saved) scan already has photos: photoFiles
  // above is only the not-yet-uploaded queue, and it's cleared right after every save, so the
  // "Фото" button/list would show 0 even with real files sitting on the document (see
  // api.listDocumentFiles, already used the same way by DocumentsPage.jsx's DocModal).
  const [attachedFiles, setAttachedFiles] = useState([]);
  // null | "list" | "camera" | { lightboxIndex } — exactly one photo-related modal at a time, so
  // opening the camera or a fullscreen photo replaces the list instead of stacking on top of it.
  const [photoView, setPhotoView] = useState(null);
  const photoInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [annotations, setAnnotations] = useState({});
  const [annotationHistory, setAnnotationHistory] = useState({}); // { [pageIdx]: Array<ann[]> }
  const annotationsRef = useRef({});
  const [scanDpi, setScanDpi] = useState(300);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [saveError, setSaveError] = useState("");

  const [wbSettings, updateWb] = useWbSettings();
  const [heatSettings, updateHeat] = useHeatSettings();

  // ── View switcher state (original / wb / heat) ───────────────────────────────
  const [scanView, setScanView] = useState("wb");
  const [viewCache, setViewCache] = useState({});
  const [viewLoading, setViewLoading] = useState(false);

  const wbSettingsRef = useRef(wbSettings);
  const heatSettingsRef = useRef(heatSettings);
  useEffect(() => {
    if (wbSettingsRef.current !== wbSettings) {
      setViewCache(prev => { const n = {}; for (const [k,v] of Object.entries(prev)) { if (!k.endsWith("_wb")) n[k] = v; } return n; });
      wbSettingsRef.current = wbSettings;
    }
  }, [wbSettings]);
  useEffect(() => {
    if (heatSettingsRef.current !== heatSettings) {
      setViewCache(prev => { const n = {}; for (const [k,v] of Object.entries(prev)) { if (!k.endsWith("_heat")) n[k] = v; } return n; });
      heatSettingsRef.current = heatSettings;
    }
  }, [heatSettings]);

  useEffect(() => {
    if (scanView === "original" || !pages.length) return;
    const key = `${currentPage}_${scanView}`;
    if (viewCache[key]) return;
    const page = pages[currentPage];
    if (!page) return;
    let cancelled = false;
    setViewLoading(true);
    (async () => {
      let result;
      if (scanView === "wb") result = await removeDarkBackground(page.base64, wbSettings);
      else if (scanView === "heat") result = await generateHeatmap(page.base64, heatSettings);
      if (!cancelled) { setViewCache(prev => ({ ...prev, [key]: result })); setViewLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [scanView, currentPage, pages]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayPages = useMemo(() => {
    if (scanView === "original") return pages;
    const key = `${currentPage}_${scanView}`;
    const override = viewCache[key];
    if (!override) return pages;
    return pages.map((p, i) => i === currentPage ? { ...p, base64: override } : p);
  }, [pages, scanView, currentPage, viewCache]);

  const [activeFoot, setActiveFoot] = useState("left");

  const [scanParams, setScanParams] = useState(INITIAL_SCAN_PARAMS);

  const [padSizes, setPadSizes] = useState([]);
  const [padSizesPerFoot, setPadSizesPerFoot] = useState({ left: null, right: null });
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  // Per-product quantity (несколько одинаковых товаров — 2+ пары) и переопределённая цена продажи
  // (ниже каталожной — YClients сам скидку не считает, см. buildGoodsTransactions на бэке).
  // Ключ — productId, значение { quantity, price }. Отсутствие записи для выбранного id значит
  // "количество 1, цена из каталога" (в т.ч. для старых сканов, сохранённых до этого поля).
  const [productDetails, setProductDetails] = useState({});
  // Snapshot of product QUANTITIES already turned into purchases for the current document — lets
  // a later re-save (editing an already-saved scan) push только ДЕЛЬТУ (сколько штук добавилось
  // с прошлого сохранения), а не переоткрывать всё заново и не пропускать новые (старый
  // wasNewDocument-гейт, который тупо скипал каждый edit-save — см. handleSaveOrUpdate below).
  const alreadyPurchasedCountsRef = useRef({});
  const documentDraftRef = useRef(null);
  const [selectedExerciseIds, setSelectedExerciseIds] = useState([]);
  // Gated on `user`, not an empty dep array — AppInner mounts before login (it just returns
  // <LoginPage/> while !user), so an unconditional fetch here always 401s on a fresh session
  // and, with no deps, never retries once the user actually logs in.
  useEffect(() => {
    if (!user) return;
    api.listPadSizes().then(setPadSizes).catch(e => console.error("listPadSizes failed:", e));
  }, [user]);

  // addPatient only — the full patient roster used to be preloaded here for client-side filtering,
  // but that stops scaling once the roster gets large, so the quick-picker below searches the
  // server on demand instead (see filteredPatients).
  const { addPatient } = usePatients();
  const [patientQuery, setPatientQuery] = useState("");
  const [showPatientDrop, setShowPatientDrop] = useState(false);
  const [showNewPatientModal, setShowNewPatientModal] = useState(false);
  const [showSaveDetailsModal, setShowSaveDetailsModal] = useState(false);
  const patientSearchRef = useRef(null);

  // Выбранный в dropdown клиент YClients переключает его во второе состояние — список его
  // записей вместо списка пациентов, пока не выберут запись (или не пропустят этот шаг).
  const [pickedYclientsClient, setPickedYclientsClient] = useState(null);
  const [yclientsRecords, setYclientsRecords] = useState(null);

  useEffect(() => {
    // pickedYclientsClient/yclientsRecords НЕ трогаем тут — они теперь управляют модалкой
    // (YClientsRecordPickerModal), которая рендерится вне patientSearchRef; клик по карточке
    // внутри неё тоже "снаружи" этого ref, так что сброс здесь закрывал бы модалку раньше, чем
    // успевал сработать выбор записи. Модалка сама закрывается через onCancel/onPick.
    const handler = (e) => {
      if (patientSearchRef.current && !patientSearchRef.current.contains(e.target)) {
        setShowPatientDrop(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const debouncedPatientQuery = useDebouncedValue(patientQuery, 250);
  const [filteredPatients, setFilteredPatients] = useState([]);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.listPatients({ q: debouncedPatientQuery, limit: 10 })
      .then(r => { if (!cancelled) setFilteredPatients(r.items); })
      .catch(() => { if (!cancelled) setFilteredPatients([]); });
    return () => { cancelled = true; };
  }, [debouncedPatientQuery, user]);

  // Совпадения по телефону/имени в YClients (филиал своей точки) — тот же debounce, что и для
  // своих пациентов, отдельным списком в том же dropdown. Молча пусто, если точка не привязана
  // к филиалу или запрос не удался — это не должно мешать обычному поиску по своей базе.
  const [yclientsMatches, setYclientsMatches] = useState([]);
  useEffect(() => {
    if (!user || !debouncedPatientQuery.trim()) { setYclientsMatches([]); return; }
    let cancelled = false;
    api.searchYclientsClient(debouncedPatientQuery)
      .then(r => { if (!cancelled) setYclientsMatches(r.clients || []); })
      .catch(() => { if (!cancelled) setYclientsMatches([]); });
    return () => { cancelled = true; };
  }, [debouncedPatientQuery, user]);

  // Записи YClients на сегодня — показываются сразу при фокусе на пустое поле (ещё до ввода):
  // на практике почти всегда именно эти пациенты и нужны сотруднику. Каждая запись уже несёт
  // данные клиента, так что выбор пункта отсюда выбирает и клиента, и запись одним кликом —
  // модалка (для неоднозначного случая поиска по телефону) тут не нужна.
  const [todayRecords, setTodayRecords] = useState(null);
  useEffect(() => {
    if (!user || !showPatientDrop || patientQuery.trim()) return;
    let cancelled = false;
    api.getYclientsRecordsToday()
      .then(r => { if (!cancelled) setTodayRecords(r.records || []); })
      .catch(() => { if (!cancelled) setTodayRecords([]); });
    return () => { cancelled = true; };
  }, [user, showPatientDrop, patientQuery]);

  const patientInputRef = useRef(null);
  const resetPatientSearch = () => {
    setShowPatientDrop(false);
    setPatientQuery("");
    setPickedYclientsClient(null);
    setYclientsRecords(null);
  };

  const handlePickTodayRecord = async (record) => {
    resetPatientSearch();
    try {
      const patient = await api.matchYclientsPatient({ yclientsClientId: record.clientId, name: record.clientName, phone: record.clientPhone });
      await switchPatient(patient.id, { yclientsRecordId: record.id });
    } catch (err) {
      setSaveError(err.response?.data?.error || "Не удалось получить данные из YClients");
    }
  };

  const handlePickYclientsClient = async (client) => {
    // Закрываем обычный дропдаун поиска — дальше ведёт модалка выбора записи (pickedYclientsClient).
    setShowPatientDrop(false);
    setPickedYclientsClient(client);
    setYclientsRecords(null);
    try {
      const patient = await api.matchYclientsPatient({ yclientsClientId: client.id, name: client.name, phone: client.phone });
      const { records } = await api.getYclientsClientRecords(client.id);
      setYclientsRecords(records);
      // Пациент уже найден/создан — храним его id тут же, чтобы выбор записи (или "без записи")
      // сразу переключил сканер на него.
      setPickedYclientsClient({ ...client, patientId: patient.id });
    } catch (err) {
      setSaveError(err.response?.data?.error || "Не удалось получить данные из YClients");
      resetPatientSearch();
    }
  };

  const handlePickYclientsRecord = async (recordId) => {
    const patientId = pickedYclientsClient?.patientId;
    if (!patientId) return;
    resetPatientSearch();
    await switchPatient(patientId, { yclientsRecordId: recordId || null });
  };

  // "Отмена" в модалке выбора записи — не выбираем пациента, просто возвращаемся к поиску (тот же
  // запрос остаётся в поле, дропдаун открывается заново).
  const handleCancelYclientsPick = () => {
    setPickedYclientsClient(null);
    setYclientsRecords(null);
    setShowPatientDrop(true);
  };

  // Selected patient is looked up by id directly (single row, indexed by primary key) rather than
  // filtered out of a preloaded list — scales the same whether there are 10 patients or 100,000.
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedPatientError, setSelectedPatientError] = useState("");
  const [patientReload, setPatientReload] = useState(0);
  useEffect(() => {
    if (!scanParams.patientId) { setSelectedPatient(null); return; }
    if (page !== "scanner") return;
    let cancelled = false;
    setSelectedPatientError("");
    api.getPatient(scanParams.patientId)
      .then(p => { if (!cancelled) setSelectedPatient(p); })
      .catch(err => { if (!cancelled) { setSelectedPatient(null); setSelectedPatientError(err.response?.data?.error || "Не удалось загрузить карточку пациента"); } });
    return () => { cancelled = true; };
  }, [scanParams.patientId, page, patientReload]);

  const navigate = (p) => setPage(p);

  const navigateToPatient = (id) => {
    setPatientCardId(id);
    setPage("patient");
  };

  // Keep ref in sync so handleAnnotationsChange doesn't close over stale state
  useEffect(() => { annotationsRef.current = annotations; }, [annotations]);

  const handleAnnotationsChange = useCallback((pageIdx, pageAnnotations) => {
    const prev = annotationsRef.current[pageIdx] || [];
    setAnnotationHistory(h => ({ ...h, [pageIdx]: [...(h[pageIdx] || []), prev] }));
    setAnnotations(a => ({ ...a, [pageIdx]: pageAnnotations }));
  }, []);

  // Replaces only the "region" annotations for a page (keeps any measurement lines the user
  // already drew) — shared by the automatic post-scan detection and the manual re-detect button.
  const applyDetectedRegions = useCallback((pageIdx, left, right) => {
    const regionAnns = [];
    if (left)  regionAnns.push({ type: "region", ...left,  foot: "left"  });
    if (right) regionAnns.push({ type: "region", ...right, foot: "right" });
    if (!regionAnns.length) return;
    const existing = annotationsRef.current[pageIdx] || [];
    const nonRegion = existing.filter(a => a.type !== "region");
    handleAnnotationsChange(pageIdx, [...nonRegion, ...regionAnns]);
  }, [handleAnnotationsChange]);

  const handleAutoDetectFeet = useCallback(async (pageIdx) => {
    const page = pagesRef.current[pageIdx];
    if (!page) return;
    const generation = imageGenerationRef.current;
    const { left, right } = await detectFootRegions(page.base64);
    if (generation === imageGenerationRef.current) applyDetectedRegions(pageIdx, left, right);
  }, [applyDetectedRegions]);

  const handleNewPages = useCallback(async (newPages, dpi) => {
    if (newPages.length !== 1) {
      setSaveError("Для одного обследования нужен один снимок обеих стоп. Повторите сканирование в планшетном режиме.");
      return;
    }
    // Always store the raw scan. Views (Белый фон / Тепловая) apply processing on demand.
    // "Обычный" (raw, with background) is no longer a selectable view — always land on "wb".
    setViewCache({});
    setScanView("wb");
    setPages(newPages);
    const generation = imageGenerationRef.current;
    setCurrentPage(0);
    setAnnotations({});
    setAnnotationHistory({});
    // Auto-place left/right region guesses on the freshly scanned page(s) — heuristic, always
    // left fully editable (drag/resize already works on region annotations).
    newPages.forEach((p, i) => {
      detectFootRegions(p.base64)
        .then(({ left, right }) => { if (generation === imageGenerationRef.current) applyDetectedRegions(i, left, right); })
        .catch(() => {});
    });
    if (dpi) setScanDpi(dpi);
    setSaveStatus(null);
    setSaveError("");
  }, [applyDetectedRegions, setPages]);

  const handleUndoAnnotation = useCallback((pageIdx) => {
    const stack = annotationHistory[pageIdx] || [];
    if (!stack.length) return;
    const restored = stack[stack.length - 1];
    setAnnotations(a => ({ ...a, [pageIdx]: restored }));
    setAnnotationHistory(h => ({ ...h, [pageIdx]: (h[pageIdx] || []).slice(0, -1) }));
  }, [annotationHistory]);

  const applyPageTransform = useCallback((pageIdx, { newSize, draw, mapPoint }) => {
    const pg = pages[pageIdx];
    if (!pg) return;
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth, h = img.naturalHeight;
      const [newW, newH] = newSize(w, h);
      const canvas = document.createElement("canvas");
      canvas.width = newW;
      canvas.height = newH;
      const ctx = canvas.getContext("2d");
      draw(ctx, img, w, h);
      const result = canvas.toDataURL("image/png");

      const mapAnns = (annArr) => annArr.map(a => {
        const [x1, y1] = mapPoint(a.x1, a.y1, w, h);
        const [x2, y2] = mapPoint(a.x2, a.y2, w, h);
        return { ...a, x1, y1, x2, y2 };
      });

      setPages(prev => prev.map((p, i) => i === pageIdx ? { ...p, base64: result } : p));
      setViewCache(prev => {
        const n = {};
        for (const [k, v] of Object.entries(prev)) { if (!k.startsWith(`${pageIdx}_`)) n[k] = v; }
        return n;
      });
      setAnnotations(a => ({ ...a, [pageIdx]: mapAnns(a[pageIdx] || []) }));
      setAnnotationHistory(h => ({ ...h, [pageIdx]: (h[pageIdx] || []).map(mapAnns) }));
    };
    img.src = pg.base64;
  }, [pages, setPages]);

  const handleFlipPage = useCallback((pageIdx) => {
    applyPageTransform(pageIdx, {
      newSize: (w, h) => [w, h],
      draw: (ctx, img, w) => { ctx.translate(w, 0); ctx.scale(-1, 1); ctx.drawImage(img, 0, 0); },
      mapPoint: (x, y, w) => [w - x, y],
    });
  }, [applyPageTransform]);

  const handleRotatePage = useCallback((pageIdx, dir) => {
    applyPageTransform(pageIdx, {
      newSize: (w, h) => [h, w],
      draw: (ctx, img, w, h) => {
        if (dir > 0) { ctx.translate(h, 0); ctx.rotate(Math.PI / 2); }
        else { ctx.translate(0, w); ctx.rotate(-Math.PI / 2); }
        ctx.drawImage(img, 0, 0);
      },
      mapPoint: (x, y, w, h) => dir > 0 ? [h - y, x] : [y, w - x],
    });
  }, [applyPageTransform]);

  const allAnnotationsFlat = () =>
    Object.entries(annotations).flatMap(([pageIdx, ann]) =>
      (ann || []).map(a => ({ ...a, page: Number(pageIdx) }))
    );

  // New saves are always complaintsFormat 2 (жалобы redesign) — footData carries the new
  // symptoms/footTypes/cornZones categories instead of the retired diseases/complaints. Old
  // scans (format 1) never go through this function again since their editor opens read-only.
  const buildFootData = (scanParams = {}) => ({
    left: {
      footSizeMm: scanParams.left?.footSizeMm ? Number(scanParams.left.footSizeMm) : null,
      symptoms: scanParams.left?.symptoms || [], footTypes: scanParams.left?.footTypes || [], cornZones: scanParams.left?.cornZones || [],
    },
    right: {
      footSizeMm: scanParams.right?.footSizeMm ? Number(scanParams.right.footSizeMm) : null,
      symptoms: scanParams.right?.symptoms || [], footTypes: scanParams.right?.footTypes || [], cornZones: scanParams.right?.cornZones || [],
    },
  });

  const buildReportPayload = async ({ patientAlias } = {}) => {
    const rawBase64 = pages[0]?.base64 || "";
    const allAnns = allAnnotationsFlat();
    const leftRegion  = allAnns.find(a => a.type === "region" && a.foot === "left");
    const rightRegion = allAnns.find(a => a.type === "region" && a.foot === "right");

    // "Обычный скан" images: background-removed (always, regardless of the "Белый фон" scanner
    // preview toggle — the report should be consistent with the heatmap, which already always
    // cleans its own background) AND with the real lines drawn on top via the same pipeline the
    // "Сканирования" preview modal uses (lib/scanPreview.js) — but WITHOUT the valik: this report
    // is patient-facing (public link + staff's "Открыть отчёт"), and the valik is an internal
    // fitting detail, not something to show the patient. Passing empty padSizes/padSizesPerFoot
    // here (instead of the real ones) is enough to suppress it — both drawAnnotations() and
    // generateReport.js's footBlock() already gate the valik on padSizes.length. DocModal's own
    // "Сканирования" preview calls buildAnnotatedFootCrops separately with the real padSizes, so
    // that internal staff view is unaffected.
    const [{ left: leftOriginal, right: rightOriginal }, leftRawCrop, rightRawCrop] = await Promise.all([
      buildAnnotatedFootCrops(rawBase64, { annotations: allAnns, padSizes: [], padSizesPerFoot: {}, dpi: scanDpi, wbSettings }),
      cropFootImage(rawBase64, leftRegion),
      cropFootImage(rawBase64, rightRegion),
    ]);

    const [leftHeat, rightHeat] = await Promise.all([
      leftRawCrop  ? generateHeatmap(leftRawCrop,  heatSettings) : Promise.resolve(null),
      rightRawCrop ? generateHeatmap(rightRawCrop, heatSettings) : Promise.resolve(null),
    ]);

    let patient = null;
    if (scanParams.patientId) {
      patient = await api.getPatient(scanParams.patientId);
    }
    if (patient && patientAlias !== undefined) patient = { ...patient, alias: patientAlias || null };

    // Inline photos as data URIs so the saved report stays self-contained — a live
    // ?token= URL would eventually 404 once that token expires (see leftOriginal/rightOriginal,
    // which are already data URIs for the same reason).
    const inlinePhoto = async (url) => {
      try {
        const resp = await fetch(url);
        const blob = await resp.blob();
        return await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    };

    let allProducts = [];
    if (selectedProductIds.length) allProducts = await api.listProducts();
    if (selectedProductIds.some(id => !allProducts.some(product => product.id === id))) {
      throw new Error("Часть выбранных товаров недоступна. Проверьте товары обследования.");
    }
    const selectedProducts = selectedProductIds.length > 0
      ? allProducts.filter(p => selectedProductIds.includes(p.id))
      : [];
    const products = await Promise.all(selectedProducts.map(async (p) => {
      // Insoles no longer carry photos — only accessories still might.
      if (p.category === "insole" || !p.hasPhoto) return { ...p, photoDataUrl: null };
      return { ...p, photoDataUrl: await inlinePhoto(api.productPhotoUrl(p.id)) };
    }));

    let loadTypes = [];
    try { loadTypes = await api.listLoadTypes(); } catch {}

    let allExercises = [];
    try { allExercises = await api.listExercises(); } catch {}
    const selectedExercises = selectedExerciseIds.length > 0
      ? allExercises.filter(e => selectedExerciseIds.includes(e.id))
      : [];
    const exercises = await Promise.all(selectedExercises.map(async (e) => {
      if (!e.hasPhoto) return { ...e, photoDataUrl: null };
      return { ...e, photoDataUrl: await inlinePhoto(api.exercisePhotoUrl(e.id)) };
    }));

    return {
      leftOriginal,
      leftHeat,
      rightOriginal,
      rightHeat,
      patient,
      params: scanParams,
      // Empty on purpose — see the comment above the buildAnnotatedFootCrops call: this report is
      // patient-facing, the valik text block (lib/generateReport.js footBlock) is gated on this
      // same padSizes.length, so leaving it empty here suppresses that block too.
      padSizes: [],
      padSizesPerFoot: {},
      products,
      productDetails,
      loadTypes,
      exercises,
      annotations: allAnns,
      dateScanned: new Date().toISOString(),
    };
  };

  const doPrint = async () => {
    try { printReport(await buildReportPayload()); }
    catch (err) { setSaveError(err.response?.data?.error || err.message || "Не удалось подготовить отчёт"); }
  };

  const handlePhotoFilesSelected = async (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file(s) later
    if (!selected.length) return;
    const read = (file) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({ operationId: crypto.randomUUID(), name: file.name, base64: reader.result });
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    try {
      const loaded = await Promise.all(selected.map(read));
      setPhotoFiles(prev => [...prev, ...loaded]);
    } catch {
      setSaveError("Не удалось прочитать выбранные файлы");
    }
  };

  // Same { name, base64 } shape as handlePhotoFilesSelected above — a webcam snapshot and a
  // file-picker upload are indistinguishable to everything downstream (LocalPhotosModal's grid,
  // api.uploadDocumentFiles at save time).
  const handleCameraCapture = (photo) => setPhotoFiles(prev => [...prev, { ...photo, operationId: crypto.randomUUID() }]);

  // Normalized view of both photo sources for LocalPhotosModal/PhotoLightboxModal — already-saved
  // server files (attachedFiles) first, then the not-yet-uploaded local queue (photoFiles). Neither
  // component needs to know which is which except for delete, handled below by `kind`.
  const allPhotos = [
    ...attachedFiles.map(f => ({ kind: "remote", id: f.id, src: api.documentFileUrl(editingDocumentId, f.id), name: f.originalName })),
    ...photoFiles.map((f, i) => ({ kind: "local", localIndex: i, src: f.base64, name: f.name })),
  ];

  const handleDeletePhoto = async (item) => {
    if (item.kind === "remote") {
      await api.deleteDocumentFile(editingDocumentId, item.id);
      setAttachedFiles(prev => prev.filter(f => f.id !== item.id));
    } else {
      const pending = photoFiles[item.localIndex];
      if (editingDocumentId && pending?.operationId) {
        try {
          const saved = (await api.listDocumentFiles(editingDocumentId)).find(file => file.operationId === pending.operationId);
          if (saved) await api.deleteDocumentFile(editingDocumentId, saved.id);
          await removeQueuedFile(editingDocumentId, pending.operationId);
        } catch (error) { setSaveError("Не удалось удалить фотографию: " + error.message); return; }
      }
      setPhotoFiles(prev => prev.filter((_, i) => i !== item.localIndex));
    }
  };

  // extraFields — мессенджер/ссылка/категория записи, собранные в модалке подтверждения
  // (SaveDetailsModal) прямо перед сохранением. Передаются явно, а не через setScanParams+doSave(),
  // потому что setScanParams асинхронный — вызов doSave() сразу следом захватил бы scanParams из
  // ещё не обновившегося рендера (устаревшее замыкание) и отправил бы прошлые/пустые значения.
  const doSave = async (extraFields = {}, preparedReport = null) => {
    if (!pages.length) return false;
    if (!scanParams.patientId) {
      setSaveError("Выберите пациента перед сохранением");
      return false;
    }
    setSaving(true);
    setSaveStatus(null);
    setSaveError("");
    const logIds = { patientId: scanParams.patientId, documentId: editingDocumentId || null };
    clientLog.info("scan", editingDocumentId
      ? `Сохранение изменений обследования ${shortId(editingDocumentId)}`
      : `Сохранение нового обследования пациента ${shortId(scanParams.patientId)}`,
      { ...logIds, pages: pages.length, products: selectedProductIds.length, photos: photoFiles.length, yclientsRecordId: scanParams.yclientsRecordId || null });
    try {
      const { patientAlias, clearMessenger, clearMessengerLink, ...scanFields } = extraFields;
      const sp = { ...scanParams, ...scanFields };
      const allAnnotations = allAnnotationsFlat();
      const hasAnyAnnotations = Object.values(annotations).some(a => a?.length > 0);
      const rawBase64 = pages[0]?.base64 || "";
      const saveBase64 = wbSettings.enabled ? await removeDarkBackground(rawBase64, wbSettings) : rawBase64;
      const composited = hasAnyAnnotations
        ? await compositeImage(saveBase64, annotations[0] || [], scanDpi)
        : saveBase64;

      const footData = buildFootData(sp);
      const report = preparedReport || await buildReportPayload({ patientAlias });
      const reportHtml = generateReportHtml({
        ...report, params: sp,
        patient: report.patient && patientAlias !== undefined
          ? { ...report.patient, alias: patientAlias || null } : report.patient,
      });
      const scanState = { scanParams: sp, padSizesPerFoot, selectedProductIds, productDetails, dpi: scanDpi };

      // Captured before editingDocumentId is (re)set below — decides both which API call to make
      // and, further down, whether this is the "first save of a new scan" for the products/purchases
      // step. Without pinning editingDocumentId to the newly-created doc right after this call,
      // every subsequent "Сохранить" click on the same screen would go through saveDocument() again
      // (POST) instead of updateDocument() (PATCH), silently creating a duplicate exam per click.

      const changes = {
        rawBase64, pdfBase64: composited, pageCount: pages.length,
        annotations: allAnnotations, footSizeMm: footData[activeFoot]?.footSizeMm || null,
        diseases: footData[activeFoot]?.symptoms || [], exercises: selectedExerciseIds, footData,
        generalSymptoms: sp.generalSymptoms || [], reportHtml, scanState,
        noteComment: sp.noteComment || null, noteRecommendations: sp.noteRecommendations || null,
        internalNote: sp.internalNote || null, yclientsRecordLabelIds: sp.recordLabelIds || [],
        messenger: sp.messenger || null, messengerLink: sp.messengerLink?.trim() || null,
        clearMessenger: Boolean(clearMessenger), clearMessengerLink: Boolean(clearMessengerLink),
        ...(patientAlias !== undefined ? { patientAlias } : {}),
      };
      const savedDoc = await saveDocumentSnapshot({
        documentId: editingDocumentId, draftRef: documentDraftRef, changes, api,
        draft: {
          ...changes, name: `Скан ${new Date().toLocaleString("ru-RU")}`,
          pdfBase64: composited, rawBase64, pageCount: pages.length, patientId: sp.patientId,
          footSide: activeFoot, complaintsFormat: 2, yclientsRecordId: sp.yclientsRecordId || null,
        },
      });
      setEditingDocumentId(savedDoc.id);
      logIds.documentId = savedDoc.id;
      clientLog.info("scan", `Обследование ${shortId(savedDoc.id)} сохранено на точке`, { ...logIds, isNew: !editingDocumentId });
      if (patientAlias !== undefined) setSelectedPatient(p => p && p.id === sp.patientId ? { ...p, alias: patientAlias || null } : p);

      const pending = await queueFiles(savedDoc.id, photoFiles);
      const state = await api.getDocumentPurchaseState(savedDoc.id);
      const allProducts = selectedProductIds.length ? await api.listProducts() : [];
      const purchases = buildPurchaseIntents(savedDoc.id, countsFromSelection(selectedProductIds, productDetails), productDetails, allProducts, state, pending, mskTodayStr());
      const result = await saveScanExtras({
        docId: savedDoc.id, patientId: scanParams.patientId, files: photoFiles, purchases, api,
        onFile: file => setAttachedFiles(previous => previous.some(f => f.id === file.id) ? previous : [...previous, file]),
      });
      setPhotoFiles(result.pendingFiles);
      alreadyPurchasedCountsRef.current = (await api.getDocumentPurchaseState(savedDoc.id)).counts;
      if (!result.pendingPurchases.length && selectedProductIds.length) {
        try { await api.pushDocumentPayment(savedDoc.id); }
        catch (error) { result.errors.push(`Оплата ожидает повторной отправки: ${error.message}`); }
      }
      setSaveStatus(result.errors.length ? "error" : "ok");
      if (result.errors.length) {
        setSaveError("Скан сохранён. " + result.errors.join("; "));
        clientLog.warn("scan", `Обследование ${shortId(savedDoc.id)} сохранено не полностью`, {
          ...logIds, errors: result.errors.slice(0, 10), pendingPhotos: result.pendingFiles.length, pendingPurchases: result.pendingPurchases.length,
        });
      }
      return result.errors.length === 0;
    } catch (err) {
      setSaveStatus("error");
      const reason = err.response?.data?.error || err.message || "Ошибка сохранения";
      setSaveError(reason);
      clientLog.error("scan", logIds.documentId
        ? `Ошибка сохранения обследования ${shortId(logIds.documentId)}: ${reason}`
        : `Ошибка сохранения обследования пациента ${shortId(logIds.patientId)}: ${reason}`,
        { ...logIds, reason, status: err.response?.status || null });
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Global Ctrl+Z on scanner page
  useEffect(() => {
    if (page !== "scanner") return;
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        handleUndoAnnotation(currentPage);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [page, currentPage, handleUndoAnnotation]);

  const handleClear = () => {
    documentDraftRef.current = null;
    setPages([]);
    setPhotoFiles([]);
    setAttachedFiles([]);
    setAnnotations({});
    setAnnotationHistory({});
    setCurrentPage(0);
    setSaveStatus(null);
    setSaveError("");
    setViewCache({});
    setScanView("wb");
    setScanParams(INITIAL_SCAN_PARAMS);
    setPadSizesPerFoot({ left: null, right: null });
    setSelectedProductIds([]);
    setProductDetails({});
    alreadyPurchasedCountsRef.current = {};
    setSelectedExerciseIds([]);
    setActiveFoot("left");
    setEditingDocumentId(null);
    setEditingReadOnly(false);
  };

  // Несохранённый скан никогда не пропадает и не уходит другому пациенту молча: любая смена
  // пациента или очистка экрана сначала спрашивает сотрудника — сохранить у текущего пациента
  // (через обычное окно «Перед сохранением»), удалить или остаться. Раньше смена пациента сама
  // сохраняла скан в обход этого окна, а ответ «другой пациент» на повторный скан выбрасывал
  // снимки (05–06.10.2026, Уточкина и Суворовский: два обследования потеряны).
  const afterSaveActionRef = useRef(null);
  // Правки после «Сохранить» (разметка, товары, упражнения, фото, параметры) тоже несохранённые:
  // запоминаем состояние скана в момент сохранения и сравниваем с текущим.
  const scanSignature = useMemo(
    () => JSON.stringify([imageRevision, annotations, scanParams, padSizesPerFoot, selectedProductIds, productDetails, selectedExerciseIds, photoFiles.length]),
    [imageRevision, annotations, scanParams, padSizesPerFoot, selectedProductIds, productDetails, selectedExerciseIds, photoFiles.length]
  );
  const [savedSignature, setSavedSignature] = useState(null);
  const [signedSaveStatus, setSignedSaveStatus] = useState(saveStatus);
  if (signedSaveStatus !== saveStatus) {
    setSignedSaveStatus(saveStatus);
    setSavedSignature(saveStatus === "ok" ? scanSignature : null);
  }
  const changedSinceSave = saveStatus === "ok" && savedSignature !== null && savedSignature !== scanSignature;
  const savedAndUnchanged = saveStatus === "ok" && !changedSinceSave;
  // The scan is already a saved document (saved here or opened from «Сканирования»): leaving
  // without saving drops only the edits, the document itself stays.
  const editsOfSavedDocument = changedSinceSave || Boolean(editingDocumentId);
  const hasUnsavedScan = pages.length > 0 && !savedAndUnchanged && !editingReadOnly;

  const runAfterLeavingScan = async (action, reason = "смена пациента") => {
    if (saving || scanning) { setSaveError("Дождитесь завершения сканирования или сохранения обследования."); return; }
    if (!hasUnsavedScan) return action();
    const choice = scanParams.patientId
      ? await confirm(`Оно сохранится у пациента «${selectedPatient?.name || "текущий пациент"}».`, {
          title: editsOfSavedDocument ? "Сохранить изменения в обследовании?" : "Сохранить текущее обследование?",
          confirmLabel: "Сохранить", altLabel: editsOfSavedDocument ? "Не сохранять" : "Удалить скан", altDanger: true,
        })
      : await confirm("У скана не выбран пациент. Выберите пациента — скан останется на экране, и его можно будет сохранить.", {
          title: "Сохранить текущий скан?", confirmLabel: "Выбрать пациента", altLabel: "Удалить скан", altDanger: true,
        });
    const scanRef = editingDocumentId ? `обследование ${shortId(editingDocumentId)}` : `скан пациента ${shortId(scanParams.patientId)}`;
    const logIds = { patientId: scanParams.patientId || null, documentId: editingDocumentId || null, reason, editsOfSavedDocument };
    if (choice === "alt") {
      clientLog.warn("scan", editsOfSavedDocument
        ? `Изменения не сохранены (${reason}): ${scanRef}`
        : `Скан удалён без сохранения (${reason}): ${scanRef}`, { ...logIds, pages: pages.length });
      return action();
    }
    clientLog.info("scan", choice === true
      ? `«Сохранить?» (${reason}): выбрано «${scanParams.patientId ? "Сохранить" : "Выбрать пациента"}» — ${scanRef}`
      : `«Сохранить?» (${reason}): отмена — ${scanRef}`, logIds);
    if (choice === true && !scanParams.patientId) {
      setPage("scanner");
      setShowPatientDrop(true);
      return;
    }
    if (choice === true) {
      afterSaveActionRef.current = action;
      setShowSaveDetailsModal(true);
    }
  };

  // Окно «Сохранить?» и для закрытия программы (electron/main.cjs спрашивает перед закрытием окна)
  // и для автовыхода по простою (AuthContext не выходит, пока скан не сохранён).
  const runAfterLeavingScanRef = useRef(runAfterLeavingScan);
  const hasUnsavedScanRef = useRef(hasUnsavedScan);
  const busyRef = useRef(false);
  useLayoutEffect(() => {
    busyRef.current = hasUnsavedScan || saving || scanning;
    runAfterLeavingScanRef.current = runAfterLeavingScan;
    hasUnsavedScanRef.current = hasUnsavedScan;
  });
  useEffect(() => {
    window.__scanappHasUnsavedScan = () => hasUnsavedScanRef.current;
    window.__scanappIsBusy = () => busyRef.current;
    window.__scanappRequestClose = () => runAfterLeavingScanRef.current(async () => {
      window.__scanappAllowClose = true;
      await clientLog.flushNow();
      window.close();
    }, "закрытие программы");
    setIdleLogoutBlocker(() => busyRef.current);
    const onBeforeUnload = (e) => {
      if (/Electron/.test(navigator.userAgent) || !busyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      delete window.__scanappHasUnsavedScan;
      delete window.__scanappIsBusy;
      delete window.__scanappRequestClose;
      setIdleLogoutBlocker(null);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [setIdleLogoutBlocker]);

  const handleLogout = () => runAfterLeavingScan(() => { handleClear(); logout(); }, "выход из учётной записи");

  // Повторный выбор того же пациента при несохранённом скане не трогает скан — только дополняет
  // параметры (например, привязку к записи). Пациент, выбранный для скана без пациента, к этому
  // скану и привязывается (кроме «Начать обследование» из карточки/Журнала — там это другой человек).
  const switchPatient = (newPatientId, scanParamsPatch = {}, { adoptOrphanScan = true } = {}) => {
    if (saving || scanning) { setSaveError("Дождитесь завершения сканирования или сохранения обследования."); return; }
    if (newPatientId && adoptOrphanScan && !scanParams.patientId && hasUnsavedScan) {
      clientLog.info("scan", `Скан без пациента привязан к пациенту ${shortId(newPatientId)}`, { patientId: newPatientId, documentId: editingDocumentId || null });
      setScanParams(prev => ({ ...prev, patientId: newPatientId, ...scanParamsPatch }));
      return;
    }
    if (newPatientId && newPatientId === scanParams.patientId && hasUnsavedScan) {
      setScanParams(prev => ({ ...prev, ...scanParamsPatch }));
      return;
    }
    return runAfterLeavingScan(() => {
      handleClear();
      if (newPatientId) setScanParams({ ...INITIAL_SCAN_PARAMS, patientId: newPatientId, ...scanParamsPatch });
    }, newPatientId ? "смена пациента" : "сброс пациента");
  };

  const startScanForPatient = (patientId) => {
    setPage("scanner");
    return switchPatient(patientId, {}, { adoptOrphanScan: false });
  };

  // «Начать обследование» из Журнала — то же самое, что startScanForPatient, но пациент ещё не
  // обязательно существует в ScanApp (find-or-create по клиенту YClients, как в handlePickTodayRecord),
  // и сразу привязываем скан к записи, из которой этот пациент был выбран.
  const startScanForYclientsRecord = async (record) => {
    try {
      const patient = await api.matchYclientsPatient({ yclientsClientId: record.clientId, name: record.clientName, phone: record.clientPhone });
      setPage("scanner");
      await switchPatient(patient.id, { yclientsRecordId: record.id }, { adoptOrphanScan: false });
    } catch (err) {
      throw new Error(err.response?.data?.error || "Не удалось получить данные из YClients", { cause: err });
    }
  };

  const startHardwareScan = async (newExam = false) => {
    const receiveScan = (newPages, dpi) => {
      if (newPages.length === 1 && newExam) {
        handleClear();
        setScanParams({ ...INITIAL_SCAN_PARAMS, patientId: scanParams.patientId, yclientsRecordId: scanParams.yclientsRecordId });
      }
      return handleNewPages(newPages, dpi);
    };
    if (mockMode) {
      const base64 = await generateMockScan();
      await receiveScan([{ fileName: "mock.jpg", base64 }], 150);
    } else {
      await doScanHW(receiveScan);
    }
  };

  const handleStartScan = async () => {
    if (!pages.length) return startHardwareScan();
    const choice = await confirm(
      "Новое обследование сохранится отдельно. Пересканирование заменит только снимок текущего обследования; жалобы, товары и фотографии сохранятся.",
      { title: "Как продолжить обследование?", confirmLabel: "Новое обследование", altLabel: "Пересканировать" }
    );
    if (choice === "alt") return startHardwareScan();
    if (choice === true) return runAfterLeavingScan(() => startHardwareScan(true), "новое обследование");
  };

  // Opens an already-saved document in the exact same scanner screen used for live scanning,
  // for editing rather than a fresh scan. doSave() then PATCHes this same document instead of
  // creating a new one (editingDocumentId is set below). Falls back gracefully for documents
  // saved before scanState/rawFilename existed — they open with the raw image + annotations
  // (already always saved) but default scan params, which still beats not opening at all.
  const loadDocumentForEditing = (doc) => {
    if (doc?.id && doc.id === editingDocumentId) { setPage("scanner"); return; }
    if (!doc?.rawFilename) {
      setSaveError("У этого скана нет исходного изображения для редактирования");
      return;
    }
    return runAfterLeavingScan(() => openDocumentForEditing(doc), "открытие другого обследования");
  };

  const openDocumentForEditing = async (doc) => {
    const resp = await fetch(api.rawFileUrl(doc.id));
    const blob = await resp.blob();
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    const byPage = {};
    for (const a of doc.annotations || []) {
      const idx = a.page ?? 0;
      (byPage[idx] ||= []).push(a);
    }

    setPages([{ base64 }]);
    setPhotoFiles([]);
    api.listDocumentFiles(doc.id).then(setAttachedFiles).catch(() => setAttachedFiles([]));
    setAnnotations(byPage);
    setAnnotationHistory({});
    setCurrentPage(0);
    setSaveStatus(null);
    setSaveError("");
    setViewCache({});
    setScanView("wb");
    setScanParams({
      ...INITIAL_SCAN_PARAMS,
      ...(doc.scanState?.scanParams || {}),
      patientId: doc.patientId || doc.scanState?.scanParams?.patientId || "",
      internalNote: doc.internalNote || "",
    });
    setPadSizesPerFoot(doc.scanState?.padSizesPerFoot || { left: null, right: null });
    setSelectedProductIds(doc.scanState?.selectedProductIds || []);
    setProductDetails(doc.scanState?.productDetails || {});
    documentDraftRef.current = null;
    const pending = await queueStorage.get(doc.id);
    setPhotoFiles(pending.files);
    alreadyPurchasedCountsRef.current = (await api.getDocumentPurchaseState(doc.id)).counts;
    setSelectedExerciseIds(doc.exercises || []);
    if (doc.scanState?.dpi) setScanDpi(doc.scanState.dpi);
    setActiveFoot(doc.footSide === "right" ? "right" : "left");
    setEditingDocumentId(doc.id);
    // Pre-жалобы-redesign scans (no complaintsFormat, or explicitly 1) never got symptoms/
    // footTypes/cornZones — reopening them for a normal edit would mean saving over real old
    // data with an empty new-format shape. View/PDF-export only for those; requires no data
    // migration and nothing about the original scan changes until someone re-scans for real.
    setEditingReadOnly((doc.complaintsFormat || 1) !== 2);
    setPage("scanner");
  };

  const handleCreatePatient = async (form) => {
    try {
      const newPatient = await addPatient(form);
      setShowNewPatientModal(false);
      await switchPatient(newPatient.id);
    } catch (err) {
      throw new Error(err.response?.data?.error || "Ошибка создания", { cause: err });
    }
  };

  // Пока карточка нового выбранного пациента ещё загружается, кнопка сканирования тоже закрыта:
  // иначе на медленной точке можно успеть начать скан до проверки обязательных данных.
  const patientReportDetailsPending = page === "scanner"
    && !editingDocumentId
    && Boolean(scanParams.patientId)
    && (selectedPatient?.id !== scanParams.patientId || needsPatientReportDetails(selectedPatient));
  const patientReportDetailsRequired = patientReportDetailsPending
    && selectedPatient?.id === scanParams.patientId;

  const savePatientReportDetails = async ({ alias, birthDate }) => {
    try {
      const updated = await api.updatePatient(selectedPatient.id, {
        alias,
        birthDate,
        baseRevision: selectedPatient.revision,
      });
      setSelectedPatient(updated);
    } catch (err) {
      // При конфликте синхронизации берём свежую ревизию, не теряя введённые в модалке значения:
      // следующий клик «Сохранить» повторит PATCH уже поверх актуальной карточки.
      if (err.response?.status === 409 && err.response.data?.current) {
        setSelectedPatient(err.response.data.current);
      }
      throw err;
    }
  };

  if (loading) return <div className="app-loading">Загрузка...</div>;
  if (!user) return <LoginPage />;

  return (
    <div className="app">
      <LeftNav page={page} navigate={navigate} user={user} logout={handleLogout} />

      <div className="app-content">
        {page === "scanner" && user?.role !== "manager" && (!user?.isCentral || editingDocumentId || FORCE_POINT_UI) && (
          <div className="scanner-screen flex flex-col flex-1 min-h-0 overflow-hidden">
            {TEST_BUILD && <div className="shrink-0 px-5 py-1 text-xs text-amber-900 bg-amber-100">Тестовая сборка · Отдельная локальная база · Синхронизация с рабочим сервером отключена · Скан-заглушка включён по умолчанию</div>}

            {/* ── Топбар — на всю ширину контентной зоны ── */}
            <div className="scanner-toolbar flex items-center flex-wrap gap-2 px-5 py-2 bg-white border-b border-slate-200 flex-shrink-0">
              {/* Scan button — no scanner hardware at central, hidden there regardless of editing mode */}
              {(!user?.isCentral || FORCE_POINT_UI) && (
                <Button
                  size="default"
                  disabled={scanning || saving || editingReadOnly || !scanParams.patientId || patientReportDetailsPending}
                  title={!scanParams.patientId
                    ? "Сначала выберите пациента"
                    : patientReportDetailsPending ? "Заполните псевдоним и дату рождения пациента" : undefined}
                  onClick={() => handleStartScan().catch(err => setSaveError(err.message || "Не удалось начать сканирование"))}
                  className={cn("h-9 px-5 font-bold flex-shrink-0 gap-2 rounded-lg", scanning && "animate-pulse bg-orange-500 hover:bg-orange-500")}
                >
                  <ScanLine className="h-4 w-4" />
                  {scanning ? "Сканирование..." : "Сканировать"}
                </Button>
              )}

              {/* Photo button */}
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handlePhotoFilesSelected}
              />
              <Button
                variant="outline"
                size="default"
                onClick={() => setPhotoView("list")}
                className="h-9 px-4 font-semibold flex-shrink-0 gap-2 rounded-lg"
              >
                <Camera className="h-4 w-4" />
                Фото{allPhotos.length > 0 ? ` (${allPhotos.length})` : ""}
              </Button>

              {/* Action buttons */}
              {pages.length > 0 && (
                <div className="flex items-center gap-1 flex-shrink-0 border-l border-slate-200 pl-3 ml-1">
                  <Button variant="ghost" size="sm" disabled={saving || scanning} onClick={() => runAfterLeavingScan(handleClear, "кнопка «Очистить»")} className="h-8 gap-1.5 text-slate-500 rounded-lg">
                    <Trash2 className="h-3.5 w-3.5" />
                    Очистить
                  </Button>
                  {!editingReadOnly && (
                    <Button
                      size="sm"
                      disabled={saving || scanning}
                      onClick={() => setShowSaveDetailsModal(true)}
                      className={cn("h-8 gap-1.5 rounded-lg", savedAndUnchanged && "bg-emerald-600 hover:bg-emerald-700")}
                    >
                      {savedAndUnchanged ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Save className="h-3.5 w-3.5" />}
                      {saving ? "Сохранение..." : savedAndUnchanged ? "Сохранено" : editsOfSavedDocument ? "Сохранить изменения" : "Сохранить"}
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" onClick={doPrint} className="h-8 gap-1.5 text-slate-500 rounded-lg">
                    <FileText className="h-3.5 w-3.5" />
                    PDF
                  </Button>
                  {editingReadOnly && (
                    <span className="text-[11px] text-amber-600 font-medium px-2 py-1 rounded-md bg-amber-50 border border-amber-200">
                      Только просмотр — старый формат
                    </span>
                  )}
                </div>
              )}

              {/* Errors */}

              {/* Patient search */}
              <div className="scanner-patient-picker relative flex items-center gap-2 flex-1 min-w-0 border-l border-slate-200 pl-4 ml-1" ref={patientSearchRef}>
                <UserSearch className="h-4 w-4 text-slate-400 flex-shrink-0" />
                {selectedPatient ? (
                  <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-2.5 py-1.5 flex-1 min-w-0">
                    <span className="text-xs font-semibold text-slate-800 truncate flex-1">{selectedPatient.name}<PatientAlias name={selectedPatient.name} alias={selectedPatient.alias} /></span>
                    {!editingDocumentId && (
                      <button
                        onClick={() => { switchPatient(""); setPatientQuery(""); }}
                        className="text-slate-400 hover:text-red-500 flex-shrink-0 transition-colors"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ) : editingDocumentId ? (
                  <span className="text-xs text-slate-400 flex-1">Пациент не выбран</span>
                ) : (
                  <div className="flex-1 relative min-w-0">
                    <Input
                      className="h-8 text-xs border-slate-200 bg-slate-50"
                      placeholder="Найдите пациента по ФИО или телефону"
                      ref={patientInputRef}
                      value={patientQuery}
                      onChange={e => { setPatientQuery(e.target.value); setShowPatientDrop(true); }}
                      onFocus={() => setShowPatientDrop(true)}
                    />
                    {showPatientDrop && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg z-50 overflow-hidden max-h-[360px] overflow-y-auto">
                        {!patientQuery.trim() && todayRecords === null && (
                          <div className="px-3 py-3 text-xs text-slate-400 text-center">Загрузка записей на сегодня...</div>
                        )}
                        {!patientQuery.trim() && todayRecords?.length > 0 && (
                          <>
                            <div className="px-3 py-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wide bg-slate-50">
                              Записи на сегодня
                            </div>
                            {todayRecords.map(r => (
                              <div key={`today-${r.id}`}
                                className="flex items-center justify-between px-3 py-2 text-xs cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-50"
                                onMouseDown={() => handlePickTodayRecord(r)}>
                                <div className="flex flex-col gap-0.5 min-w-0">
                                  <span className="font-medium text-slate-800 truncate">{r.clientName}<PatientAlias name={r.clientName} alias={r.scanAppPatientAlias} /></span>
                                  <span className="text-slate-400 truncate">{r.services?.join(", ") || "Без услуги"}</span>
                                </div>
                                <span className="text-slate-400 flex-shrink-0 ml-2">{r.date?.slice(11, 16)}</span>
                              </div>
                            ))}
                          </>
                        )}
                        {yclientsMatches.length > 0 && (
                          <>
                            <div className="px-3 py-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wide bg-slate-50">
                              Найдено в YClients
                            </div>
                            {yclientsMatches.map(c => (
                              <div key={`yc-${c.id}`}
                                className="flex items-center justify-between px-3 py-2 text-xs cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-50"
                                onMouseDown={() => handlePickYclientsClient(c)}>
                                <span className="font-medium text-slate-800">{c.name}</span>
                                {c.phone && <span className="text-slate-400">{c.phone}</span>}
                              </div>
                            ))}
                          </>
                        )}
                        {filteredPatients.length === 0 && yclientsMatches.length === 0
                          ? <div className="px-3 py-3 text-xs text-slate-400 text-center">Нет результатов</div>
                          : filteredPatients.map(p => (
                            <div key={p.id}
                              className="flex items-center justify-between px-3 py-2 text-xs cursor-pointer hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
                              onMouseDown={() => { switchPatient(p.id); setPatientQuery(""); setShowPatientDrop(false); }}>
                              <span className="font-medium text-slate-800">{p.name}<PatientAlias name={p.name} alias={p.alias} /></span>
                              {p.phone && <span className="text-slate-400">{p.phone}</span>}
                            </div>
                          ))
                        }
                        <div
                          className="flex items-center gap-1.5 px-3 py-2 text-xs text-primary cursor-pointer hover:bg-primary/5 border-t border-slate-100 font-medium"
                          onMouseDown={() => { setShowPatientDrop(false); setShowNewPatientModal(true); }}
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Новый пациент
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Test mode toggle */}
              {user?.role === "admin" && (
                <div className="flex items-center gap-2 flex-shrink-0 border-l border-slate-200 pl-4 ml-1">
                  <Switch id="test-mode" checked={mockMode} onCheckedChange={setMockMode} className="scale-90" />
                  <Label htmlFor="test-mode" className="text-xs text-slate-400 cursor-pointer font-medium">Тест</Label>
                </div>
              )}
            </div>

            {(saveError || (scanError && !saving && !mockMode)) && (
              <div role="alert" className="px-5 py-2 text-sm text-red-700 bg-red-50 border-b border-red-100 shrink-0 break-words">
                {saveError || scanError}
              </div>
            )}
            {selectedPatientError && (
              <LoadError message={selectedPatientError} onRetry={() => setPatientReload(v => v + 1)} />
            )}

            {savedAndUnchanged && editingDocumentId && <ScanSavedPanel
              documentId={editingDocumentId}
              isCentral={user?.isCentral}
              onNextPatient={() => { handleClear(); resetPatientSearch(); setShowPatientDrop(true); requestAnimationFrame(() => patientInputRef.current?.focus()); }}
            />}

            {/* ── Основная рабочая область с отступами ── */}
            <div className="scanner-workspace flex flex-1 min-h-0 gap-5 p-6 overflow-hidden" style={{ background: "#F1F5F9" }}>

              {/* Слева: зона сканирования */}
              <div className="flex flex-col flex-1 min-w-0 rounded-2xl overflow-hidden" style={{ background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05)" }}>
                {viewLoading && scanView !== "original" && <div className="view-loading-bar" />}
                <ImageViewer
                  pages={displayPages}
                  imageKey={imageRevision}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  dpi={scanDpi}
                  annotations={annotations[currentPage] || []}
                  onAnnotationsChange={(ann) => handleAnnotationsChange(currentPage, ann)}
                  onUndo={() => handleUndoAnnotation(currentPage)}
                  canUndo={(annotationHistory[currentPage] || []).length > 0}
                  onFlipPage={() => handleFlipPage(currentPage)}
                  onRotatePage={(dir) => handleRotatePage(currentPage, dir)}
                  onAutoDetectFeet={() => handleAutoDetectFeet(currentPage)}
                  activeFoot={activeFoot}
                  onActiveFootChange={setActiveFoot}
                  scanView={scanView}
                  onScanViewChange={setScanView}
                  padSizes={padSizes}
                  onPadSizeChange={setPadSizesPerFoot}
                  scanParams={scanParams}
                  onScanParamsChange={setScanParams}
                  allAnnotations={Object.values(annotations).flat()}
                />
              </div>

              {/* Справа: блоки параметров */}
              <div className="scanner-details flex flex-col gap-4 flex-shrink-0 min-h-0 overflow-hidden">

                {/* Блок 1: Жалобы / Обувь и нагрузка */}
                <div className="flex flex-col rounded-2xl overflow-hidden min-h-0" style={{ flex: 1, background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.05)" }}>
                  <ScanParamsPanel
                    params={scanParams}
                    onChange={setScanParams}
                    onNavigateToPatient={navigateToPatient}
                    onNavigate={navigate}
                    selectedPatient={selectedPatient}
                    readOnly={editingReadOnly}
                  />
                </div>

                {/* Товары занимают постоянную видимую область, включая пустое состояние. */}
                <div className="scanner-products-slot flex flex-col gap-4">
                  <ScannerProductsCard
                    onNavigate={navigate}
                    selectedIds={selectedProductIds}
                    onSelectionChange={setSelectedProductIds}
                    productDetails={productDetails}
                    onProductDetailsChange={setProductDetails}
                  />
                  {/* Упражнения временно скрыты из сканера по просьбе пользователя — сама фича
                      (выбор/сохранение/печать в отчёте) не убрана, просто не показывается тут. */}
                </div>
              </div>
            </div>
          </div>
        )}

        {page === "patients" && <PatientsPage onSelectPatient={navigateToPatient} />}

        {page === "patient" && patientCardId && (
          <PatientCard patientId={patientCardId} onBack={() => setPage("patients")} onEditDocument={loadDocumentForEditing} onStartScan={startScanForPatient} />
        )}

        {page === "scans" && <DocumentsPage onNavigateToPatient={navigateToPatient} onEditDocument={loadDocumentForEditing} />}

        {page === "journal" && ((user?.role !== "manager" && !user?.isCentral) || FORCE_POINT_UI) && (
          <JournalPage onStartExam={startScanForYclientsRecord} onNavigateToPatient={navigateToPatient} />
        )}

        {page === "settings" && !user?.isCentral && <SettingsPage heatSettings={heatSettings} updateHeat={updateHeat} />}

        {page === "admin" && user?.role === "admin" && <AdminPage />}

        {page === "fieldconfig" && user?.role === "admin" && <FieldConfigPage />}

        {page === "yclients" && user?.role === "admin" && <YClientsPage />}

        {page === "reconcile" && user?.role === "admin" && user?.isCentral && <ReconcilePage />}
        {page === "sync-log" && user?.role === "admin" && user?.isCentral && <SyncLogPage />}
        {page === "point-logs" && user?.role === "admin" && user?.isCentral && <PointLogsPage />}

        {page === "lab" && user?.role === "admin" && <ScanLabPage />}

        {page === "roadmap" && user?.role === "admin" && <RoadmapPage />}
      </div>

      {showNewPatientModal && (
        <NewPatientModal
          onClose={() => setShowNewPatientModal(false)}
          onCreate={handleCreatePatient}
        />
      )}

      {patientReportDetailsRequired && (
        <PatientReportDetailsModal
          key={selectedPatient.id}
          patient={selectedPatient}
          onSave={savePatientReportDetails}
          onCancel={() => { setScanParams(prev => ({ ...prev, patientId: "", yclientsRecordId: null })); resetPatientSearch(); setShowPatientDrop(true); }}
        />
      )}

      {showSaveDetailsModal && (
        <SaveDetailsModal
          scanParams={scanParams}
          patient={selectedPatient}
          saving={saving}
          error={saveError}
          loadPreview={buildReportPayload}
          submitLabel={editingDocumentId ? "Сохранить изменения" : "Сохранить обследование"}
          onClose={() => { afterSaveActionRef.current = null; setShowSaveDetailsModal(false); }}
          onSubmit={async (fields, preview) => {
            const { patientAlias, clearMessenger, clearMessengerLink, ...scanFields } = fields; // eslint-disable-line no-unused-vars
            setScanParams(prev => ({ ...prev, ...scanFields }));
            const saved = await doSave(fields, preview);
            if (!saved) return;
            setShowSaveDetailsModal(false);
            const next = afterSaveActionRef.current;
            afterSaveActionRef.current = null;
            if (saved && next) await next();
          }}
        />
      )}

      {pickedYclientsClient && (
        <YClientsRecordPickerModal
          client={pickedYclientsClient}
          records={yclientsRecords}
          onPick={handlePickYclientsRecord}
          onCancel={handleCancelYclientsPick}
        />
      )}

      {photoView === "list" && (
        <LocalPhotosModal
          files={allPhotos}
          onDelete={handleDeletePhoto}
          onAddClick={() => photoInputRef.current?.click()}
          onCameraClick={() => setPhotoView("camera")}
          onPhotoClick={(i) => setPhotoView({ lightboxIndex: i })}
          onClose={() => setPhotoView(null)}
        />
      )}

      {photoView === "camera" && (
        <CameraCaptureModal
          onCapture={handleCameraCapture}
          onClose={() => setPhotoView("list")}
        />
      )}

      {photoView?.lightboxIndex != null && (
        <PhotoLightboxModal
          src={allPhotos[photoView.lightboxIndex]?.src}
          alt={allPhotos[photoView.lightboxIndex]?.name}
          onClose={() => setPhotoView("list")}
        />
      )}

      <ConfirmDialogElement />
    </div>
  );
}
